import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useBreadcrumb } from "@/contexts/BreadcrumbContext";
import { useApp } from "@/contexts/AppContext";
import { StripeTerminalConnection } from "@/components/StripeTerminalConnection";
import { StripeTerminalPayment } from "@/components/StripeTerminalPayment";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";

interface CollectPaymentLineItem {
  description?: string;
  quantity?: number;
  price?: number;
}

interface CollectPaymentOrder {
  _id: string;
  amount: number;
  currency?: string;
  customerEmail?: string;
  paymentIntentId?: string;
  status?: string;
  purchasedProducts?: CollectPaymentLineItem[];
}

const PAYMENT_CONFIRM_TIMEOUT_MS = 5000;

function sleep(ms: number) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

async function waitForPaymentComplete({
  paymentId,
  timeoutMs = PAYMENT_CONFIRM_TIMEOUT_MS,
}: {
  paymentId: string;
  timeoutMs?: number;
}): Promise<boolean> {
  const deadline = Date.now() + timeoutMs;
  let backoffMs = 100;

  while (true) {
    try {
      const res = await fetch(`/api/payments/${paymentId}`);
      if (res.ok) {
        const data = (await res.json()) as { payment?: { status?: string } };
        if (data.payment?.status === "complete") {
          return true;
        }
      }
    } catch (error) {
      console.error(error);
    }

    const remaining = deadline - Date.now();
    if (remaining <= 0) {
      return false;
    }

    const waitMs = Math.min(backoffMs, remaining);
    await sleep(waitMs);
    backoffMs = Math.min(backoffMs * 2, 2000);
  }
}

function formatAmount(amount: number, currency?: string) {
  const formatter = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: (currency || "usd").toUpperCase(),
  });
  return formatter.format(amount / 100);
}

function lineDisplayName(line: CollectPaymentLineItem) {
  const desc = line.description?.trim();
  return desc || "Item";
}

function lineQuantity(line: CollectPaymentLineItem) {
  const qty = line.quantity ?? 1;
  return qty > 0 ? qty : 1;
}

function lineUnitPriceCents(line: CollectPaymentLineItem) {
  if (typeof line.price !== "number") return null;
  return line.price;
}

function lineTotalCents(line: CollectPaymentLineItem) {
  const unit = lineUnitPriceCents(line);
  if (unit == null) return null;
  return unit * lineQuantity(line);
}

function OrderSummaryTable({
  lines,
  totalAmount,
  currency,
  customerEmail,
}: {
  lines: CollectPaymentLineItem[];
  totalAmount: number;
  currency?: string;
  customerEmail?: string;
}) {
  return (
    <section className="rounded-lg border">
      {customerEmail ? (
        <div className="border-b px-4 py-3 text-sm text-muted-foreground">{customerEmail}</div>
      ) : null}
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Item</TableHead>
            <TableHead className="w-16 text-right">Qty</TableHead>
            <TableHead className="w-28 text-right">Price</TableHead>
            <TableHead className="w-28 text-right">Amount</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {lines.length === 0 ? (
            <TableRow>
              <TableCell colSpan={4} className="text-muted-foreground">
                No line items
              </TableCell>
            </TableRow>
          ) : null}
          {lines.map((line, index) => {
            const qty = lineQuantity(line);
            const unit = lineUnitPriceCents(line);
            const lineTotal = lineTotalCents(line);
            return (
              <TableRow key={index}>
                <TableCell className="font-medium">{lineDisplayName(line)}</TableCell>
                <TableCell className="text-right tabular-nums">{qty}</TableCell>
                <TableCell className="text-right tabular-nums text-muted-foreground">
                  {unit != null ? formatAmount(unit, currency) : "—"}
                </TableCell>
                <TableCell className="text-right tabular-nums">
                  {lineTotal != null ? formatAmount(lineTotal, currency) : "—"}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
        <TableFooter>
          <TableRow>
            <TableCell colSpan={3} className="text-right font-medium">
              Total
            </TableCell>
            <TableCell className="text-right font-semibold tabular-nums">
              {formatAmount(totalAmount, currency)}
            </TableCell>
          </TableRow>
        </TableFooter>
      </Table>
    </section>
  );
}

export function CollectPaymentRoute() {
  const { id } = useParams<{ id: string }>();
  const { setBreadcrumbs, clearBreadcrumbs } = useBreadcrumb();
  const { stripeConnected, stripeAccountLoading, connectedReader } = useApp();
  const navigate = useNavigate();
  const [payment, setPayment] = useState<CollectPaymentOrder | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isConfirmingPayment, setIsConfirmingPayment] = useState(false);

  useEffect(() => {
    async function load() {
      if (!id) return;
      setIsLoading(true);

      try {
        const paymentResponse = await fetch(`/api/payments/${id}`);

        if (paymentResponse.ok) {
          const paymentData = await paymentResponse.json();
          setPayment(paymentData.payment ?? null);
        } else {
          setPayment(null);
        }
      } catch (error) {
        console.error(error);
        setPayment(null);
      } finally {
        setIsLoading(false);
      }
    }

    load();
  }, [id]);

  useEffect(() => {
    if (!id) return;

    setBreadcrumbs([
      { label: "Sales", path: "/products" },
      { label: "Orders", path: "/sales/orders" },
      { label: "Collect payment", path: `/sales/orders/${id}/collect` },
    ]);

    return () => {
      clearBreadcrumbs();
    };
  }, [id, setBreadcrumbs, clearBreadcrumbs]);

  const handlePaymentIntentCreated = (paymentIntentId: string) => {
    setPayment((current) =>
      current ? { ...current, paymentIntentId } : current,
    );
  };

  const handlePaymentSuccess = async () => {
    if (!id) return;

    setIsConfirmingPayment(true);
    try {
      const complete = await waitForPaymentComplete({ paymentId: id });
      if (complete) {
        toast.success("Payment recorded");
        navigate("/sales/orders");
        return;
      }

      toast.error(
        "Payment was taken but confirmation timed out. Check orders for the updated status.",
      );
    } finally {
      setIsConfirmingPayment(false);
    }
  };

  if (isLoading || stripeAccountLoading) {
    return (
      <div className="mx-auto flex min-h-[40vh] w-full max-w-4xl items-center justify-center px-4 py-8">
        <Loader2 className="h-12 w-12 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!payment) {
    return (
      <div className="mx-auto w-full max-w-4xl space-y-4 px-4 py-8">
        <p className="text-muted-foreground">Order not found.</p>
        <Button variant="outline" asChild>
          <Link to="/sales/orders">Back to orders</Link>
        </Button>
      </div>
    );
  }

  if (!stripeConnected) {
    return (
      <div className="mx-auto w-full max-w-4xl space-y-4 px-4 py-8">
        <h1 className="text-2xl font-bold">Collect payment</h1>
        <p className="text-muted-foreground">
          Connect a Stripe account before collecting in-person payments.
        </p>
        <Button asChild>
          <Link to="/settings/integrations/stripe">Connect Stripe</Link>
        </Button>
      </div>
    );
  }

  if (!connectedReader) {
    return (
      <div className="mx-auto w-full max-w-4xl space-y-6 px-4 py-8">
        <h1 className="text-2xl font-bold">Collect payment</h1>
        <p className="text-muted-foreground">
          Connect a Stripe Terminal reader before collecting payment. Power on your reader and
          discover it below.
        </p>
        <div className="rounded-lg border p-4">
          <StripeTerminalConnection showTitle={false} />
        </div>
      </div>
    );
  }

  const lineItems = payment.purchasedProducts?.filter((p) => p.description?.trim()) ?? [];

  return (
    <div className="mx-auto w-full max-w-4xl space-y-6 px-4 py-8">
      <OrderSummaryTable
        lines={lineItems}
        totalAmount={payment.amount}
        currency={payment.currency}
        customerEmail={payment.customerEmail}
      />

      {isConfirmingPayment ? (
        <div className="flex items-center justify-center gap-2 py-4 text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          Confirming payment...
        </div>
      ) : (
        <StripeTerminalPayment
          paymentId={payment._id}
          amount={payment.amount}
          currency={payment.currency || "usd"}
          onPaymentIntentCreated={handlePaymentIntentCreated}
          onPaymentSuccess={handlePaymentSuccess}
        />
      )}
    </div>
  );
}
